import { BaseBenefitPlanPolicyPeriod } from "@platform/openapi-types";

import type { BenefitPlanGroup, RfpCoverage } from "../api/types";

/**
 * Collects the benefit plan IDs belonging to a plan group.
 */
export const collectPlanIdsForGroup = (
  planGroupId: string,
  benefitPlanGroups: BenefitPlanGroup[] | undefined,
): Set<string> => {
  const group = benefitPlanGroups?.find((g) => g.id === planGroupId);
  return new Set(group?.benefitPlans?.map((plan) => plan.id) ?? []);
};

/**
 * A coverage must always have at least one current or newBusiness BPG.
 * Returns true when deleting the given group would remove the last one.
 *
 * Returns false when benefitPlanGroups is undefined (data not yet loaded)
 * — the backend enforces this constraint regardless.
 */
export const isLastCurrentOrNewBusinessGroup = (
  planGroupId: string,
  benefitPlanGroups: BenefitPlanGroup[] | undefined,
): boolean => {
  if (!benefitPlanGroups) {
    return false;
  }

  const group = benefitPlanGroups.find((g) => g.id === planGroupId);
  const period = group?.policyPeriod;

  const isCurrentOrNewBusiness =
    period === BaseBenefitPlanPolicyPeriod.current ||
    period === BaseBenefitPlanPolicyPeriod.newBusiness;

  if (!isCurrentOrNewBusiness) {
    return false;
  }

  return !benefitPlanGroups.some(
    (g) => g.id !== planGroupId && g.policyPeriod === period,
  );
};

/**
 * Returns true when deleting the given group would remove the last BPG
 * in the coverage — regardless of policy period.
 *
 * Returns false when benefitPlanGroups is undefined (data not yet loaded)
 * — the backend enforces this constraint regardless.
 */
export const isLastPlanGroup = (
  planGroupId: string,
  benefitPlanGroups: BenefitPlanGroup[] | undefined,
): boolean => {
  if (!benefitPlanGroups) {
    return false;
  }

  return (
    benefitPlanGroups.length === 1 && benefitPlanGroups[0].id === planGroupId
  );
};

/**
 * Returns a new coverage with dirty flags cleared for the given plan IDs.
 * The benefit plans themselves are preserved so their values remain visible
 * if the backend rejects the delete.
 *
 * Returns `undefined` if no dirty flags needed clearing (caller can skip the
 * state update).
 */
export const clearDirtyFlagsForPlans = (
  coverage: RfpCoverage,
  planIds: ReadonlySet<string>,
): RfpCoverage | undefined => {
  if (planIds.size === 0) return undefined;
  if (!coverage._dirtyPlanIds?.length) return undefined;

  const filtered = coverage._dirtyPlanIds.filter((id) => !planIds.has(id));
  if (filtered.length === coverage._dirtyPlanIds.length) return undefined;

  return {
    ...coverage,
    _dirtyPlanIds: filtered.length > 0 ? filtered : undefined,
  };
};

/**
 * Filters an array removing the given ID, returning undefined if nothing changed.
 */
function filterById<T>(
  items: T[] | undefined,
  id: string,
  getId: (item: T) => string,
): { next: T[]; changed: boolean } {
  const arr = items ?? [];
  const next = arr.filter((item) => getId(item) !== id);
  return { next, changed: next.length !== arr.length };
}

/**
 * Removes a benefit plan from local coverage state, including ordering and
 * pending dirty markers that still reference the deleted plan.
 *
 * Returns `undefined` when the plan does not exist anywhere in the coverage.
 */
export const removePlanFromCoverage = (
  coverage: RfpCoverage,
  benefitPlanId: string,
): RfpCoverage | undefined => {
  const plans = filterById(
    coverage.benefitPlans,
    benefitPlanId,
    (p) => p.id ?? "",
  );
  const order = filterById(coverage.columnOrder, benefitPlanId, (id) => id);
  const dirty = filterById(coverage._dirtyPlanIds, benefitPlanId, (id) => id);

  if (!plans.changed && !order.changed && !dirty.changed) {
    return undefined;
  }

  return {
    ...coverage,
    ...(plans.changed && { benefitPlans: plans.next }),
    ...(order.changed && { columnOrder: order.next }),
    ...(dirty.changed && {
      _dirtyPlanIds: dirty.next.length > 0 ? dirty.next : undefined,
    }),
  };
};
